import { fetchClient } from "@/api/api";
import type {
	getUserSettingsInput,
	getUserSettingsOutput,
	updateUserSettingsInput,
	updateUserSettingsOutput,
} from "@/api/custom_types";
import { useApiError, useTranslation } from "@/hooks";
import { normalizeEmptyStringsToNull } from "@/utils/normalize";
import { queryClient } from "@/utils/queryClient";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { toast } from "@st-marketplace/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { SETTINGS_SERVICE_KEYS } from "./keys";

const useGetSettings: CustomQueryHook<
	getUserSettingsOutput,
	getUserSettingsInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: SETTINGS_SERVICE_KEYS.useGetUserSettings(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/dashboard/settings/{user_id}",
				{ params: { path: payload.path }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
	});
};

const useUpdateSettings: CustomMutationHook<
	updateUserSettingsInput,
	updateUserSettingsOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: SETTINGS_SERVICE_KEYS.useUpdateUserSettings(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/marketplace/dashboard/settings/{user_id}",
				{
					params: { path: payload.path },
					body: normalizeEmptyStringsToNull(payload.body),
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("settings.updateSuccessMessage"));
			queryClient.invalidateQueries({
				queryKey: SETTINGS_SERVICE_KEYS.useGetUserSettings(),
			});
		},
	});
};

export { useGetSettings, useUpdateSettings };
